import React, { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import IsAuthenticated from "./components/common/isAuthenticated";
import RequirePayment from "./components/common/RequirePayment";

import Layout from "./components/common/Layout";
import LandingPage from "./pages/landingPage";

// After a fresh deploy, any tab still running the previous build references
// chunk file names with the old hash. The static host responds to those
// unknown paths with the SPA's index.html, so the dynamic import() throws
// either ChunkLoadError or "'text/html' is not a valid JavaScript MIME type".
// That's exactly the blank-screen-on-logout symptom we were seeing. Detect
// it and force ONE hard reload so the tab picks up the new HTML+chunks.
const lazyWithReload = (factory) =>
  lazy(() =>
    factory().catch((err) => {
      const msg = String(err?.message || "");
      const isStaleChunk =
        err?.name === "ChunkLoadError" ||
        /Loading chunk|Loading CSS chunk|Failed to fetch dynamically imported module|dynamically imported module|'text\/html' is not a valid JavaScript MIME type|Importing a module script failed/i.test(
          msg
        );
      if (isStaleChunk) {
        const KEY = "__eqn_chunk_reload_at__";
        const last = Number(sessionStorage.getItem(KEY) || 0);
        // Guard against a reload loop if the new build is still broken.
        if (!last || Date.now() - last > 10_000) {
          sessionStorage.setItem(KEY, String(Date.now()));
          window.location.reload();
          return { default: () => null };
        }
      }
      throw err;
    })
  );

// Lazy-load all non-home routes to shrink the initial JS bundle
const UserProfile = lazyWithReload(() => import("./pages/userProfile"));
const Dashboard = lazyWithReload(() => import("./pages/dashboard"));
const Wallet = lazyWithReload(() => import("./pages/wallet"));
const Rewards = lazyWithReload(() => import("./pages/rewards"));
const Transactions = lazyWithReload(() => import("./pages/transactions"));
const Privacy = lazyWithReload(() => import("./pages/privacy"));
const Terms = lazyWithReload(() => import("./pages/terms"));
const ChildSafetyStandards = lazyWithReload(() => import("./pages/childSafetyStandards"));
const DeleteData = lazyWithReload(() => import("./pages/deleteData"));
const BankDetails = lazyWithReload(() => import("./pages/bankDetails"));
const NodeDetails = lazyWithReload(() => import("./pages/nodeDetails"));
const Signin = lazyWithReload(() => import("./pages/auth/Signin"));
const OtpVerification = lazyWithReload(() => import("./pages/auth/OtpVerification"));
const CommunityGroup = lazyWithReload(() => import("./pages/communityGroup"));
const OtherProfile = lazyWithReload(() => import("./pages/otherProfile"));
const Inbox = lazyWithReload(() => import("./pages/inbox"));
const NodeAccess = lazyWithReload(() => import("./pages/nodeaccess"));
const TwoFactAuth = lazyWithReload(() => import("./pages/twoFactAuth"));
const MyNode = lazyWithReload(() => import("./pages/myNode"));
const MainWallet = lazyWithReload(() => import("./pages/mainWallet"));
const WalletMethod = lazyWithReload(() => import("./pages/walletMethod"));
const Login = lazyWithReload(() => import("./pages/auth/Login"));
const JoinNode = lazyWithReload(() => import("./pages/joinNode"));
const PaymentSucess = lazyWithReload(() => import("./pages/PaymentSucess"));
const UserInfo = lazyWithReload(() => import("./components/Auth/UserInfo"));
const ForgotPassword = lazyWithReload(() => import("./pages/auth/forgotPassword"));
const ErrorPage = lazyWithReload(() => import("./pages/errorPage"));
const HowItWorks = lazyWithReload(() => import("./pages/howitworks"));
const Search = lazyWithReload(() => import("./pages/Search"));
const Onboarding = lazyWithReload(() => import("./pages/onboarding"));

import "./Fonts/transfonter.org-20240226-081850/stylesheet.css";

function App() {
  const location = useLocation();
  const routeLength = location.pathname.split("/")[1].length;

  return (
      <Suspense fallback={null}>
        <Routes>
          <Route element={<Layout />}>
            <Route element={<IsAuthenticated />}>
              {/*
                /onboarding lives INSIDE the auth guard (user must be signed
                in) but OUTSIDE <RequirePayment> (an unpaid user has to be
                able to reach the page that lets them pay). Everything else
                requires a settled payment status when the feature flag is on.
              */}
              <Route path="/onboarding" element={<Onboarding />} />

              <Route element={<RequirePayment />}>
                <Route path="/profile" element={<UserProfile />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/wallet" element={<Wallet />} />
                <Route path="/mainwallet" element={<MainWallet />} />
                <Route path="/walletmethod" element={<WalletMethod />} />
                <Route path="/rewards" element={<Rewards />} />
                <Route path="/transactions" element={<Transactions />} />
                {/* <Route path="/my-nodes" element={<MyNodes />} /> */}
                <Route path="/bank-details" element={<BankDetails />} />
                <Route path="/twofact-auth" element={<TwoFactAuth />} />
                <Route path="/other-profile/" element={<OtherProfile />} />
                <Route path="/payment-sucess" element={<PaymentSucess />} />
                <Route path="/inbox" element={<Inbox />} />
                <Route path="/my-node" element={<MyNode />} />
                <Route path="/user-info" element={<UserInfo />} />
                <Route path="*" element={<ErrorPage />} />
              </Route>
            </Route>

            <Route path="/" element={<LandingPage />} />
            <Route path="/sign-in" element={<Signin />} />
            <Route path="/otp-verification" element={<OtpVerification />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/community-group" element={<CommunityGroup />} />

            {/* Admin auth aliases */}
            <Route path="/admin/login" element={<Login />} />
            <Route path="/admin/sign-in" element={<Signin />} />

            {/* Legacy support route redirects to public community */}
            <Route path="/support" element={<Navigate to="/community-group" replace />} />
            <Route path="/support/" element={<Navigate to="/community-group" replace />} />

            {/* Privacy route */}
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/privacy/" element={<Privacy />} />

            {/* Terms route */}
            <Route path="/terms" element={<Terms />} />
            <Route path="/terms/" element={<Terms />} />

            {/* Child safety standards route (Google Play policy requirement) */}
            <Route path="/child-safety-standards" element={<ChildSafetyStandards />} />
            <Route path="/child-safety-standards/" element={<ChildSafetyStandards />} />
            <Route path="/child-safety" element={<Navigate to="/child-safety-standards" replace />} />
            <Route path="/child-safety/" element={<Navigate to="/child-safety-standards" replace />} />

            {/* Data deletion route */}
            <Route path="/deletedata" element={<DeleteData />} />
            <Route path="/deletedata/" element={<DeleteData />} />
            <Route
              path="/deletedata.html"
              element={<Navigate to="/deletedata" replace />}
            />

            {/* Node Access route */}
            <Route path="/node-access" element={<NodeAccess />} />

            {/* How It Works route */}
            <Route path="/howitworks" element={<HowItWorks />} />
            <Route path="/howitworks/" element={<HowItWorks />} />

            {/* P1.11 — dedicated campaign search results page */}
            <Route path="/search" element={<Search />} />


            {/* Public campaign view — accessible to logged-out and unpaid
                users so they can browse before signing in. The page itself
                gates owner-only chrome via `effectiveIsOwner`, and the Join
                CTA in <UserNodeStatsCard> already routes anonymous viewers
                through /sign-in?intent=join-campaign. */}
            <Route path="/node-details" element={<NodeDetails />} />
            {routeLength === 24 ? (
              <Route path="/:id" element={<JoinNode />} />
            ) : (
              <Route path="*" element={<ErrorPage />} />
            )}
          </Route>
        </Routes>
      </Suspense>
  );
}

export default App;